import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useLang } from "@/lib/i18n";
import api, { formatApiError } from "@/lib/api";
import AuthShell from "@/components/AuthShell";
import { toast } from "sonner";
import { Check, Eye, EyeOff } from "lucide-react";

export default function Forgot() {
  const { t } = useLang();
  const nav = useNavigate();
  const [step, setStep] = useState(0); // 0 mobile, 1 number, 2 image, 3 password
  const [mobile, setMobile] = useState("");
  const [challenge, setChallenge] = useState(null);
  const [num, setNum] = useState(null);
  const [img, setImg] = useState(null);
  const [pw, setPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const imgMeta = (id) => challenge?.images_meta?.find((m) => m.id === id);

  const start = async (e) => {
    e.preventDefault(); setErr("");
    if (!/^[6-9]\d{9}$/.test(mobile)) return setErr("Enter a valid 10-digit mobile.");
    setBusy(true);
    try {
      const { data } = await api.post("/auth/forgot/start", { mobile });
      setChallenge(data); setStep(1);
    } catch (e2) { setErr(formatApiError(e2.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const reset = async () => {
    setErr("");
    if (pw !== confirm) return setErr("Passwords do not match.");
    if (!(pw.length >= 8 && /[A-Za-z]/.test(pw) && /\d/.test(pw))) return setErr("Password needs 8+ chars with a letter and a number.");
    setBusy(true);
    try {
      await api.post("/auth/forgot/reset", { challenge_id: challenge.challenge_id, number: num, image: img, new_password: pw });
      toast.success("Password reset! Please login.");
      nav("/login");
    } catch (e2) { setErr(formatApiError(e2.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <AuthShell title="Recover Password" subtitle="Use your secret number and image">
      {step === 0 && (
        <form onSubmit={start} className="space-y-4">
          <div>
            <label className="font-semibold text-sm">{t("mobile")}</label>
            <input className="input-thali mt-1" inputMode="numeric" maxLength={10}
              value={mobile} onChange={(e) => setMobile(e.target.value.replace(/\D/g, ""))} data-testid="forgot-mobile" />
          </div>
          {err && <div className="text-red-600 text-sm bg-red-50 rounded-lg p-2" data-testid="forgot-error">{err}</div>}
          <button disabled={busy} className="btn-3d-green w-full" data-testid="forgot-start">{busy ? "…" : "Continue"}</button>
          <p className="text-center text-sm"><Link to="/login" className="text-thali-green font-bold">Back to {t("login")}</Link></p>
        </form>
      )}

      {step === 1 && challenge && (
        <div className="space-y-4">
          <p className="text-sm text-thali-ink/70 text-center">Tap your <b>secret number</b>.</p>
          <div className="grid grid-cols-3 gap-3">
            {challenge.numbers.map((n) => (
              <button key={n} onClick={() => setNum(n)} data-testid={`forgot-num-${n}`}
                className={`aspect-square rounded-2xl font-num font-extrabold text-3xl grid place-items-center transition-all ${num === n ? "bg-thali-saffron text-white ring-4 ring-thali-saffron/30 scale-105" : "bg-thali-cream hover:bg-thali-turmeric/30"}`}>
                {String(n).padStart(3, "0")}
              </button>
            ))}
          </div>
          <button disabled={num === null} onClick={() => setStep(2)} className="btn-3d-green w-full" data-testid="forgot-num-next">Next</button>
        </div>
      )}

      {step === 2 && challenge && (
        <div className="space-y-4">
          <p className="text-sm text-thali-ink/70 text-center">Tap your <b>secret image</b>.</p>
          <div className="grid grid-cols-3 gap-3">
            {challenge.images.map((id) => (
              <button key={id} onClick={() => setImg(id)} data-testid={`forgot-img-${id}`}
                className={`aspect-square rounded-2xl grid place-items-center transition-all ${img === id ? "bg-thali-saffron/20 ring-4 ring-thali-saffron/40 scale-105" : "bg-thali-cream hover:bg-thali-turmeric/30"}`}>
                <span className="text-5xl">{imgMeta(id)?.emoji}</span>
              </button>
            ))}
          </div>
          <button disabled={!img} onClick={() => setStep(3)} className="btn-3d-green w-full" data-testid="forgot-img-next">Next</button>
        </div>
      )}

      {step === 3 && (
        <div className="space-y-4">
          <p className="text-sm text-thali-ink/70 text-center">Set your new password.</p>
          <div className="relative">
            <input className="input-thali pr-12" type={show ? "text" : "password"} placeholder="New password"
              value={pw} onChange={(e) => setPw(e.target.value)} data-testid="forgot-newpw" />
            <button type="button" onClick={() => setShow(!show)} className="absolute right-3 top-1/2 -translate-y-1/2 text-thali-ink/50" aria-label="Toggle">
              {show ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
            </button>
          </div>
          <div className="relative">
            <input className="input-thali pr-10" type={show ? "text" : "password"} placeholder="Confirm password"
              value={confirm} onChange={(e) => setConfirm(e.target.value)} data-testid="forgot-confirm" />
            {confirm && <span className="absolute right-3 top-1/2 -translate-y-1/2">{confirm === pw ? <Check className="w-5 h-5 text-green-600" /> : null}</span>}
          </div>
          {err && <div className="text-red-600 text-sm bg-red-50 rounded-lg p-2" data-testid="forgot-reset-error">{err}</div>}
          <button disabled={busy} onClick={reset} className="btn-3d-green w-full" data-testid="forgot-reset">{busy ? "…" : "Reset Password"}</button>
        </div>
      )}
    </AuthShell>
  );
}
